import { OpenAPIV3 } from 'openapi-types'
import { joi } from '@nhtio/lucid-resourceful/joi'
import type { AlternativesSchema, ObjectSchema } from '@nhtio/lucid-resourceful/joi'

// Helper schemas for reusable components
const ReferenceObjectSchema = joi.object<OpenAPIV3.ReferenceObject>({
  $ref: joi.string().required(),
})

const ExternalDocumentationObjectSchema = joi.object<OpenAPIV3.ExternalDocumentationObject>({
  description: joi.string().optional(),
  url: joi.string().uri().required(),
})

const ContactObjectSchema = joi.object<OpenAPIV3.ContactObject>({
  name: joi.string().optional(),
  url: joi.string().uri().optional(),
  email: joi.string().email().optional(),
})

const LicenseObjectSchema = joi.object<OpenAPIV3.LicenseObject>({
  name: joi.string().required(),
  url: joi.string().uri().optional(),
})

const InfoObjectSchema = joi.object<OpenAPIV3.InfoObject>({
  title: joi.string().required(),
  description: joi.string().optional(),
  termsOfService: joi.string().optional(),
  contact: ContactObjectSchema.optional(),
  license: LicenseObjectSchema.optional(),
  version: joi.string().required(),
})

const ServerVariableObjectSchema = joi.object<OpenAPIV3.ServerVariableObject>({
  enum: joi.array().items(joi.string()).optional(),
  default: joi.string().required(),
  description: joi.string().optional(),
})

const ServerObjectSchema = joi.object<OpenAPIV3.ServerObject>({
  url: joi.string().required(),
  description: joi.string().optional(),
  variables: joi.object().pattern(joi.string(), ServerVariableObjectSchema).optional(),
})

const XMLObjectSchema = joi.object<OpenAPIV3.XMLObject>({
  name: joi.string().optional(),
  namespace: joi.string().optional(),
  prefix: joi.string().optional(),
  attribute: joi.boolean().optional(),
  wrapped: joi.boolean().optional(),
})

const DiscriminatorObjectSchema = joi.object<OpenAPIV3.DiscriminatorObject>({
  propertyName: joi.string().required(),
  mapping: joi.object().pattern(joi.string(), joi.string()).optional(),
})

const ExampleObjectSchema = joi.object<OpenAPIV3.ExampleObject>({
  summary: joi.string().optional(),
  description: joi.string().optional(),
  value: joi.any().optional(),
  externalValue: joi.string().optional(),
})

// Forward declare schemas that will be defined later for circular references
let SchemaObjectSchema: AlternativesSchema<OpenAPIV3.SchemaObject>
let MediaTypeObjectSchema: ObjectSchema<OpenAPIV3.MediaTypeObject>
let PathItemObjectSchema: ObjectSchema<OpenAPIV3.PathItemObject>
let CallbackObjectSchema: ObjectSchema<OpenAPIV3.CallbackObject>

// Define base schema object without recursive references first
const BaseSchemaObjectProperties = {
  title: joi.string().optional(),
  description: joi.string().optional(),
  format: joi.string().optional(),
  default: joi.any().optional(),
  multipleOf: joi.number().optional(),
  maximum: joi.number().optional(),
  exclusiveMaximum: joi.boolean().optional(),
  minimum: joi.number().optional(),
  exclusiveMinimum: joi.boolean().optional(),
  maxLength: joi.number().integer().min(0).optional(),
  minLength: joi.number().integer().min(0).optional(),
  pattern: joi.string().optional(),
  maxItems: joi.number().integer().min(0).optional(),
  minItems: joi.number().integer().min(0).optional(),
  uniqueItems: joi.boolean().optional(),
  maxProperties: joi.number().integer().min(0).optional(),
  minProperties: joi.number().integer().min(0).optional(),
  required: joi.array().items(joi.string()).optional(),
  enum: joi.array().optional(),
  nullable: joi.boolean().optional(),
  discriminator: DiscriminatorObjectSchema.optional(),
  readOnly: joi.boolean().optional(),
  writeOnly: joi.boolean().optional(),
  xml: XMLObjectSchema.optional(),
  externalDocs: ExternalDocumentationObjectSchema.optional(),
  example: joi.any().optional(),
  deprecated: joi.boolean().optional(),
}

// Schema Object - defined with link references
SchemaObjectSchema = joi
  .alternatives<OpenAPIV3.SchemaObject>()
  .try(
    // ArraySchemaObject
    joi.object<OpenAPIV3.ArraySchemaObject>({
      type: joi.string().valid('array').required(),
      items: joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')).required(),
      ...BaseSchemaObjectProperties,
      additionalProperties: joi
        .alternatives()
        .try(joi.boolean(), ReferenceObjectSchema, joi.link('#schemaObject'))
        .optional(),
      properties: joi
        .object()
        .pattern(
          joi.string(),
          joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject'))
        )
        .optional(),
      allOf: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')))
        .optional(),
      oneOf: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')))
        .optional(),
      anyOf: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')))
        .optional(),
      not: joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')).optional(),
    }),
    // NonArraySchemaObject
    joi.object<OpenAPIV3.NonArraySchemaObject>({
      type: joi.string().valid('boolean', 'object', 'number', 'string', 'integer').optional(),
      ...BaseSchemaObjectProperties,
      additionalProperties: joi
        .alternatives()
        .try(joi.boolean(), ReferenceObjectSchema, joi.link('#schemaObject'))
        .optional(),
      properties: joi
        .object()
        .pattern(
          joi.string(),
          joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject'))
        )
        .optional(),
      allOf: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')))
        .optional(),
      oneOf: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')))
        .optional(),
      anyOf: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')))
        .optional(),
      not: joi.alternatives().try(ReferenceObjectSchema, joi.link('#schemaObject')).optional(),
    })
  )
  .id('schemaObject') as AlternativesSchema<OpenAPIV3.SchemaObject>

const SchemaOrReferenceObjectSchema = joi
  .alternatives<OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject>()
  .try(ReferenceObjectSchema, SchemaObjectSchema)

const HeaderObjectSchema = joi.object<OpenAPIV3.HeaderObject>({
  description: joi.string().optional(),
  required: joi.boolean().optional(),
  deprecated: joi.boolean().optional(),
  allowEmptyValue: joi.boolean().optional(),
  style: joi.string().optional(),
  explode: joi.boolean().optional(),
  allowReserved: joi.boolean().optional(),
  schema: joi.alternatives().try(ReferenceObjectSchema, SchemaObjectSchema).optional(),
  example: joi.any().optional(),
  examples: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, ExampleObjectSchema))
    .optional(),
  content: joi.object().pattern(joi.string(), joi.link('#mediaTypeObject')).optional(),
})

const ParameterObjectSchema = joi.object<OpenAPIV3.ParameterObject>({
  name: joi.string().required(),
  in: joi.string().required(),
  description: joi.string().optional(),
  required: joi.boolean().optional(),
  deprecated: joi.boolean().optional(),
  allowEmptyValue: joi.boolean().optional(),
  style: joi.string().optional(),
  explode: joi.boolean().optional(),
  allowReserved: joi.boolean().optional(),
  schema: joi.alternatives().try(ReferenceObjectSchema, SchemaObjectSchema).optional(),
  example: joi.any().optional(),
  examples: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, ExampleObjectSchema))
    .optional(),
  content: joi.object().pattern(joi.string(), joi.link('#mediaTypeObject')).optional(),
})

const EncodingObjectSchema = joi.object<OpenAPIV3.EncodingObject>({
  contentType: joi.string().optional(),
  headers: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, HeaderObjectSchema))
    .optional(),
  style: joi.string().optional(),
  explode: joi.boolean().optional(),
  allowReserved: joi.boolean().optional(),
})

MediaTypeObjectSchema = joi
  .object<OpenAPIV3.MediaTypeObject>({
    schema: joi.alternatives().try(ReferenceObjectSchema, SchemaObjectSchema).optional(),
    example: joi.any().optional(),
    examples: joi
      .object()
      .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, ExampleObjectSchema))
      .optional(),
    encoding: joi.object().pattern(joi.string(), EncodingObjectSchema).optional(),
  })
  .id('mediaTypeObject')

const LinkObjectSchema = joi.object<OpenAPIV3.LinkObject>({
  operationRef: joi.string().optional(),
  operationId: joi.string().optional(),
  parameters: joi.object().pattern(joi.string(), joi.any()).optional(),
  requestBody: joi.any().optional(),
  description: joi.string().optional(),
  server: ServerObjectSchema.optional(),
})

const ResponseObjectSchema = joi.object<OpenAPIV3.ResponseObject>({
  description: joi.string().required(),
  headers: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, HeaderObjectSchema))
    .optional(),
  content: joi.object().pattern(joi.string(), MediaTypeObjectSchema).optional(),
  links: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, LinkObjectSchema))
    .optional(),
})

const ResponsesObjectSchema = joi
  .object<OpenAPIV3.ResponsesObject>()
  .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, ResponseObjectSchema))

const RequestBodyObjectSchema = joi.object<OpenAPIV3.RequestBodyObject>({
  description: joi.string().optional(),
  content: joi.object().pattern(joi.string(), MediaTypeObjectSchema).required(),
  required: joi.boolean().optional(),
})

const SecurityRequirementObjectSchema = joi
  .object<OpenAPIV3.SecurityRequirementObject>()
  .pattern(joi.string(), joi.array().items(joi.string()))

const OperationObjectSchema = <T extends {} = {}>() =>
  joi
    .object<OpenAPIV3.OperationObject<T>>({
      tags: joi.array().items(joi.string()).optional(),
      summary: joi.string().optional(),
      description: joi.string().optional(),
      externalDocs: ExternalDocumentationObjectSchema.optional(),
      operationId: joi.string().optional(),
      parameters: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, ParameterObjectSchema))
        .optional(),
      requestBody: joi
        .alternatives()
        .try(ReferenceObjectSchema, RequestBodyObjectSchema)
        .optional(),
      responses: ResponsesObjectSchema.required(),
      callbacks: joi
        .object()
        .pattern(
          joi.string(),
          joi.alternatives().try(ReferenceObjectSchema, joi.link('#callbackObject'))
        )
        .optional(),
      deprecated: joi.boolean().optional(),
      security: joi.array().items(SecurityRequirementObjectSchema).optional(),
      servers: joi.array().items(ServerObjectSchema).optional(),
    })
    .unknown(true) // Allow additional properties for extension

PathItemObjectSchema = (<T extends {} = {}>() =>
  joi
    .object<OpenAPIV3.PathItemObject<T>>({
      $ref: joi.string().optional(),
      summary: joi.string().optional(),
      description: joi.string().optional(),
      servers: joi.array().items(ServerObjectSchema).optional(),
      parameters: joi
        .array()
        .items(joi.alternatives().try(ReferenceObjectSchema, ParameterObjectSchema))
        .optional(),
      // HTTP method operations
      get: OperationObjectSchema<T>().optional(),
      put: OperationObjectSchema<T>().optional(),
      post: OperationObjectSchema<T>().optional(),
      delete: OperationObjectSchema<T>().optional(),
      options: OperationObjectSchema<T>().optional(),
      head: OperationObjectSchema<T>().optional(),
      patch: OperationObjectSchema<T>().optional(),
      trace: OperationObjectSchema<T>().optional(),
    })
    .unknown(true)
    .id('pathItemObject'))() as ObjectSchema<OpenAPIV3.PathItemObject> // Allow additional properties for extension

CallbackObjectSchema = joi
  .object<OpenAPIV3.CallbackObject>()
  .pattern(joi.string(), PathItemObjectSchema)
  .id('callbackObject')

const PathsObjectSchema = <T extends {} = {}, P extends {} = {}>() =>
  joi
    .object<OpenAPIV3.PathsObject<T, P>>()
    .pattern(joi.string(), PathItemObjectSchema.allow(undefined))

const TagObjectSchema = joi.object<OpenAPIV3.TagObject>({
  name: joi.string().required(),
  description: joi.string().optional(),
  externalDocs: ExternalDocumentationObjectSchema.optional(),
})

// Security Scheme Objects
const HttpSecuritySchemeSchema = joi.object<OpenAPIV3.HttpSecurityScheme>({
  type: joi.string().valid('http').required(),
  description: joi.string().optional(),
  scheme: joi.string().required(),
  bearerFormat: joi.string().optional(),
})

const ApiKeySecuritySchemeSchema = joi.object<OpenAPIV3.ApiKeySecurityScheme>({
  type: joi.string().valid('apiKey').required(),
  description: joi.string().optional(),
  name: joi.string().required(),
  in: joi.string().required(),
})

const OAuth2SecuritySchemeSchema = joi.object<OpenAPIV3.OAuth2SecurityScheme>({
  type: joi.string().valid('oauth2').required(),
  description: joi.string().optional(),
  flows: joi
    .object({
      implicit: joi
        .object({
          authorizationUrl: joi.string().uri().required(),
          refreshUrl: joi.string().uri().optional(),
          scopes: joi.object().pattern(joi.string(), joi.string()).required(),
        })
        .optional(),
      password: joi
        .object({
          tokenUrl: joi.string().uri().required(),
          refreshUrl: joi.string().uri().optional(),
          scopes: joi.object().pattern(joi.string(), joi.string()).required(),
        })
        .optional(),
      clientCredentials: joi
        .object({
          tokenUrl: joi.string().uri().required(),
          refreshUrl: joi.string().uri().optional(),
          scopes: joi.object().pattern(joi.string(), joi.string()).required(),
        })
        .optional(),
      authorizationCode: joi
        .object({
          authorizationUrl: joi.string().uri().required(),
          tokenUrl: joi.string().uri().required(),
          refreshUrl: joi.string().uri().optional(),
          scopes: joi.object().pattern(joi.string(), joi.string()).required(),
        })
        .optional(),
    })
    .required(),
})

const OpenIdSecuritySchemeSchema = joi.object<OpenAPIV3.OpenIdSecurityScheme>({
  type: joi.string().valid('openIdConnect').required(),
  description: joi.string().optional(),
  openIdConnectUrl: joi.string().uri().required(),
})

const SecuritySchemeObjectSchema = joi
  .alternatives<OpenAPIV3.SecuritySchemeObject>()
  .try(
    HttpSecuritySchemeSchema,
    ApiKeySecuritySchemeSchema,
    OAuth2SecuritySchemeSchema,
    OpenIdSecuritySchemeSchema
  )

const ComponentsObjectSchema = joi.object<OpenAPIV3.ComponentsObject>({
  schemas: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, SchemaObjectSchema))
    .optional(),
  responses: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, ResponseObjectSchema))
    .optional(),
  parameters: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, ParameterObjectSchema))
    .optional(),
  examples: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, ExampleObjectSchema))
    .optional(),
  requestBodies: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, RequestBodyObjectSchema))
    .optional(),
  headers: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, HeaderObjectSchema))
    .optional(),
  securitySchemes: joi
    .object()
    .pattern(
      joi.string(),
      joi.alternatives().try(ReferenceObjectSchema, SecuritySchemeObjectSchema)
    )
    .optional(),
  links: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, LinkObjectSchema))
    .optional(),
  callbacks: joi
    .object()
    .pattern(joi.string(), joi.alternatives().try(ReferenceObjectSchema, CallbackObjectSchema))
    .optional(),
})

// Main Document Schema
export const OpenAPIV3DocumentSchema = <T extends {} = {}>() =>
  joi
    .object<OpenAPIV3.Document<T>>({
      'openapi': joi
        .string()
        .required()
        .pattern(/^3\.\d+\.\d+$/),
      'info': InfoObjectSchema.required(),
      'servers': joi.array().items(ServerObjectSchema).optional(),
      'paths': PathsObjectSchema<T>().required(),
      'components': ComponentsObjectSchema.optional(),
      'security': joi.array().items(SecurityRequirementObjectSchema).optional(),
      'tags': joi.array().items(TagObjectSchema).optional(),
      'externalDocs': ExternalDocumentationObjectSchema.optional(),
      'x-express-openapi-additional-middleware': joi.array().items(joi.function()).optional(),
      'x-express-openapi-validation-strict': joi.boolean().optional(),
    })
    .unknown(true) // Allow additional properties for extension

// Export individual schemas for reuse
export {
  InfoObjectSchema,
  ContactObjectSchema,
  LicenseObjectSchema,
  ServerObjectSchema,
  ServerVariableObjectSchema,
  PathsObjectSchema,
  PathItemObjectSchema,
  OperationObjectSchema,
  ExternalDocumentationObjectSchema,
  ParameterObjectSchema,
  HeaderObjectSchema,
  SchemaObjectSchema,
  SchemaOrReferenceObjectSchema,
  DiscriminatorObjectSchema,
  XMLObjectSchema,
  ReferenceObjectSchema,
  ExampleObjectSchema,
  MediaTypeObjectSchema,
  EncodingObjectSchema,
  RequestBodyObjectSchema,
  ResponsesObjectSchema,
  ResponseObjectSchema,
  LinkObjectSchema,
  CallbackObjectSchema,
  SecurityRequirementObjectSchema,
  ComponentsObjectSchema,
  SecuritySchemeObjectSchema,
  HttpSecuritySchemeSchema,
  ApiKeySecuritySchemeSchema,
  OAuth2SecuritySchemeSchema,
  OpenIdSecuritySchemeSchema,
  TagObjectSchema,
}
